import { WorkExperience } from "@/components/user-sheet/profile-editors/WorkExperience";
import { AddLogisticsToEvaluationControl } from "./AddLogisticsToEvaluationControl";

import { Column, Row, SheetSectionHeader, Text } from "@/components";

import { useUserSheet } from "@/components/user-sheet/userSheetContext";
import { dates } from "@/lib/dates";
import { keywords } from "@/lib/keywords";
import { relevanceCosineScoreToRating } from "@/lib/relevanceScore";
import { cn } from "@/lib/utils";
import { ClassifiedKeyword, WorkExperienceType } from "@/types";

const CandidacyExperienceRow = ({
  highlightKeywords,
  workExperience,
}: {
  highlightKeywords?: ClassifiedKeyword[];
  workExperience: WorkExperienceType;
}) => {
  /* Context */
  const { candidacyId } = useUserSheet();

  /* Helpers */
  const dateDisplay =
    dates.prepareDateDisplayForCertificationOrEducationOrWorkExperience({
      current: workExperience.currentPosition,
      endMonth: workExperience.endMonth,
      endYear: workExperience.endYear,
      startMonth: workExperience.startMonth,
      startYear: workExperience.startYear,
    }) || "—";
  const tenure =
    dates.calculateTenureForCertificationOrEducationOrWorkExperience({
      current: workExperience.currentPosition,
      endMonth: workExperience.endMonth,
      endYear: workExperience.endYear,
      startMonth: workExperience.startMonth,
      startYear: workExperience.startYear,
    }) || "—";
  const company =
    workExperience.internalCompany?.name.trim() ??
    workExperience.company?.trim() ??
    "Unknown company";
  const title = workExperience.title?.trim() ?? "Role";
  const relevanceRating = relevanceCosineScoreToRating(
    workExperience.relevanceCosineScore,
  );
  const highlightKeywordText = highlightKeywords
    ?.map(({ keyword }) => keyword)
    .join(" ");

  /* Render */
  // Strong matches (4-5) read green; weaker ones stay neutral so they don't
  // compete with the role text. Unscored or irrelevant experience shows nothing.
  const renderRelevanceChip = () => {
    if (!relevanceRating) return null;

    return (
      <Text
        className={cn(
          "rounded-full px-2 py-0.5 text-xs font-medium",
          relevanceRating >= 4
            ? "bg-green-50 text-green-700"
            : "bg-slate-100 text-slate-600",
        )}
      >
        {relevanceRating}/5
      </Text>
    );
  };

  const render = () => {
    return (
      <Column className="group/evaluation">
        <WorkExperience
          dateActions={
            candidacyId ? (
              <AddLogisticsToEvaluationControl
                candidacyId={candidacyId}
                subtitle={[dateDisplay, tenure].filter(Boolean).join(" · ")}
                title={`${title} — ${company}`}
              />
            ) : null
          }
          highlightKeywords={highlightKeywordText}
          titleContent={
            <Row className="min-w-0 flex-wrap items-center gap-1.5">
              <Text className="text-sm font-medium capitalize text-slate-900">
                {title}
              </Text>
              {renderRelevanceChip()}
            </Row>
          }
          workExperience={workExperience}
        />
      </Column>
    );
  };

  return render();
};

export const CandidacyExperienceSection = ({
  highlightKeywords = [],
  workExperiences,
}: {
  highlightKeywords?: ClassifiedKeyword[];
  workExperiences: WorkExperienceType[];
}) => {
  /* Helpers */
  const needsFollowUpKeywords = highlightKeywords.filter(
    ({ category, keyword }) => {
      if (!category) return true;

      return !workExperiences.some((workExperience) =>
        workExperience.descriptions?.some((description) =>
          keywords.matchesText({ keyword, text: description }),
        ),
      );
    },
  );

  /* Render */
  const renderNeedsFollowUp = () => {
    if (!needsFollowUpKeywords.length) return null;

    return (
      <Column className="gap-2 pt-1">
        <Row className="items-center gap-2">
          <Text className="text-sm font-normal text-slate-700">
            Needs follow-up
          </Text>
          <Text className="rounded bg-amber-50 px-1.5 py-0.5 text-[11px] font-normal text-amber-700">
            Needs follow-up
          </Text>
        </Row>
        <Row className="flex-wrap items-center gap-1.5">
          {needsFollowUpKeywords.map(({ keyword }) => (
            <Text
              className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-xs font-normal text-slate-600"
              key={keyword}
            >
              {keyword}
            </Text>
          ))}
        </Row>
      </Column>
    );
  };

  const render = () => {
    return (
      <Column className="gap-6">
        <SheetSectionHeader title="Experience" />
        <Column className="gap-7">
          {workExperiences.map((workExperience) => (
            <CandidacyExperienceRow
              highlightKeywords={highlightKeywords}
              key={workExperience.id}
              workExperience={workExperience}
            />
          ))}
        </Column>
        {renderNeedsFollowUp()}
      </Column>
    );
  };

  return render();
};
